import {ArtfactKind} from '@mrp-brasil/nick-data-commons';
import {IContext} from '@mrp-brasil/nick-node-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import {EMPTY, from, Observable, of} from 'rxjs';
import {map, concatMap, toArray, delay} from 'rxjs/operators';

@HookClass('flow')
export class FlowHook extends BaseHook {

    @Hook('ce1d71eb-7e4d-4987-af71-b482ef56b6ae')
    public execute(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return this.service('FlowService')
            .pipe(concatMap((service: any) => {
                const currentFlow = this.context.getData('flowContext');

                if (inputArtifact.object?.config?.queue) {
                    return this.context.clone()
                        .pipe(concatMap((context: IContext) => {
                            service.configureInstanceId(context.instanceId);
                            return service.startClientNewFlow(inputArtifact.object.processId, inputArtifact.object.data, inputArtifact.object.config);
                        }));
                }

                return service.startNewFlow(inputArtifact.object.processId, inputArtifact.object.data, {_id: currentFlow._id, entity: currentFlow.entity} as any);
            }))
            .pipe(map((result: any) => {
                const artifact = new NickProcessArtfact();
                artifact.kind = ArtfactKind.ANY;
                artifact.object = result;
                return artifact;
            }));
    }

    @Hook('a98a4ca4-d94d-4378-8d52-8f6433fdec9f')
    public forEach(inputArtifact: NickProcessArtfact): Observable<any> {
        if (!(inputArtifact.object.data instanceof Array) && !isNaN(inputArtifact.object.data))
            inputArtifact.object.data = new Array(parseInt(inputArtifact.object.data)).fill({}).map((i: any, index: number) => ({__index: index}));

        if (inputArtifact.object.config?.parseArray) {
            if (inputArtifact.object.data != undefined && !(inputArtifact.object.data instanceof Array))
                inputArtifact.object.data = [inputArtifact.object.data];
        }

        if (!(inputArtifact.object.data instanceof Array))
            return of([]);

        return from(inputArtifact.object.data)
            .pipe(map((item: any, index: number) => {
                const artifact = new NickProcessArtfact();
                artifact.kind = ArtfactKind.ANY;
                artifact.object = item;
                return artifact;
            }), toArray())
            .pipe(map((items: NickProcessArtfact[]) => {
                (items as any).___async = inputArtifact.object.config?.async == true || inputArtifact.object.config?.async == undefined;
                (items as any).___batch = inputArtifact.object.config?.batch;

                items.forEach((artifact, index: number) => {
                    if (artifact.object && !(typeof artifact.object == 'string') && !(typeof artifact.object == 'number') && !(typeof artifact.object == 'boolean'))
                        artifact.object.__index = index;
                });

                return items;
            }));
    }

    @Hook('f43af2a5-8b98-4261-b579-6f03bd430400')
    public stop(inputArtifact: NickProcessArtfact): Observable<any> {
        const currentFlow = this.context.getData('flowContext');
        const parentFlow = currentFlow.parentFlow;
        parentFlow.stop(inputArtifact.object.config.pieceId, inputArtifact.object.__flowContext.currentPiece, inputArtifact.object.__flowContext.pieceId);

        currentFlow.endOn = this.context.getData('flowContext').pieceId;
        currentFlow.status = 3;
        currentFlow.end$.next(inputArtifact);
        currentFlow.end$.complete();

        return of([]);
    }

    @Hook('e6219329-5d08-42cb-80fa-0293dafb09a6')
    public group(inputArtifact: NickProcessArtfact): Observable<any> {
        return of(inputArtifact)
            .pipe(map(() => {
                const currentFlow = this.context.getData('flowContext');
                const parentFlow = currentFlow.parentFlow;

                if (parentFlow && parentFlow.status != 2 && parentFlow.status == 1) {
                    parentFlow.status = 2;
                    parentFlow.continue(inputArtifact.object.config.pieceId, inputArtifact.object.__flowContext.currentPiece, inputArtifact.object.__flowContext.pieceId, inputArtifact.object.config);
                }

                currentFlow.endOn = this.context.getData('flowContext').pieceId;
                currentFlow.status = 3;
                currentFlow.end$.next(inputArtifact);
                currentFlow.end$.complete();
                return [];
            }));
    }

    @Hook('2eff1ad2-4810-48bb-bae1-c1d194fa8013')
    public waitForOutputs(inputArtifact: NickProcessArtfact): Observable<any> {
        const currentFlow = this.context.getData('flowContext');

        if (!currentFlow.__doWaitPieces)
            currentFlow.__doWaitPieces = new Map<string, boolean>();

        inputArtifact.object.timeout = inputArtifact.object.timeout ? inputArtifact.object.timeout : 28000;

        const waitOutputs = inputArtifact.object.outputs
            ? inputArtifact.object.outputs
            : currentFlow.piecesTo.get(inputArtifact.object.__flowContext.inputId);

        const allExecuted = () => {
            return waitOutputs.every((outputId: string) => {
                return currentFlow.status == 3 || currentFlow.outputs.has(outputId);
            });
        };

        const timeout$ = of(inputArtifact)
            .pipe(delay(inputArtifact.object.timeout))
            .pipe(concatMap(() => {
                // if (!allExecuted()) {
                //     currentFlow.__doWaitPieces.set(inputArtifact.object.__flowContext.inputId, true);
                //     throw new Error('Wait Piece timeout');
                // }

                if (currentFlow.status == 3)
                    return EMPTY;

                if (!currentFlow.__doWaitPieces.has(inputArtifact.object.__flowContext.inputId)) {
                    currentFlow.__doWaitPieces.set(inputArtifact.object.__flowContext.inputId, true);
                    throw new Error('Wait Piece timeout');
                }

                return EMPTY;
            }));

        if (!allExecuted())
            return timeout$;

        if (currentFlow.__doWaitPieces.has(inputArtifact.object.__flowContext.inputId))
            return EMPTY;

        if (currentFlow.status == 3)
            return EMPTY;

        currentFlow.__doWaitPieces.set(inputArtifact.object.__flowContext.inputId, true);

        const artifact = new NickProcessArtfact();
        artifact.kind = ArtfactKind.ANY;
        artifact.object = {
            pieces: waitOutputs
        };

        return of(artifact);
    }


}
